import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import PageHeader from '../../components/PageHeader.jsx';
import StatusBadge from '../../components/StatusBadge.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import SortableTh from '../../components/SortableTh.jsx';
import { useTableSort } from '../../lib/tableSort.js';
import { ComplaintsAPI } from '../../lib/api';
import { complaintSerial } from '../../lib/complaintSerial';
import { useAuth } from '../../lib/auth.jsx';
import {
  buildPath, COMPLAINT_FILTERS, COMPLAINT_STATUS, complaintCapabilities,
} from '../../config';

export default function ComplaintsList() {
  const { filter = 'my_complaints' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const can = complaintCapabilities(user);

  // An external (Auretics portal) user only ever has ONE view — the
  // complaints they filed — because the backend scopes their queryset
  // to their own rows.  Offering "My Team" / "Created By Me" would just
  // render the same list under three names, so we collapse the picker.
  const filters = can.canSeeTeam
    ? COMPLAINT_FILTERS
    : COMPLAINT_FILTERS.filter((f) => f.value === 'my_complaints');


  // Status lives in the query string (not component state) so the
  // dashboard's stat tiles can deep-link to a single status — clicking
  // "Pending: 12" should land on those 12 rows, not the unfiltered list.
  // It also makes the filtered view bookmarkable and refresh-proof.
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || '';
  const setStatusFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('status', value); else next.delete('status');
    setSearchParams(next, { replace: true });
  };

  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const params = useMemo(() => {
    const p = { ordering: '-created_on' };
    if (filter === 'my_complaints') p.mine = 1;
    if (search) p.search = search;
    if (statusFilter) p.status = statusFilter;
    return p;
  }, [filter, search, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ComplaintsAPI.list(params)
      .then((data) => !cancelled && setItems(data.results || data))
      .catch((e) => !cancelled && setError(e))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  const { sortedRows, sort, toggleSort } = useTableSort(items, {
    getValue: (row, key) => {
      if (key === 'ticket') return complaintSerial(row);
      if (key === 'title') return row.complaint_title;
      if (key === 'division') return row.division_name;
      if (key === 'assigned') return row.assign_to_email;
      if (key === 'status') return row.status;
      return row[key];
    },
  });

  return (
    <>
      <PageHeader
        title={can.isExternal ? 'My Complaints' : 'Complaints'}
        subtitle={
          can.isExternal
            ? 'Every complaint you have raised, and where each one stands.'
            : can.isIndividual
              ? 'Track and resolve complaints assigned to you.'
              : 'Track and resolve complaints assigned to you or your team.'
        }
        actions={<Link className="ty-btn" to="/complaints/create">+ Create Complaint</Link>}
      />

      <div className="ty-filters">
        {/* Only render the scope picker when there's more than one scope
            to pick — see ``filters`` above. */}
        {filters.length > 1 && (
          <select
            className="ty-select"
            value={filter}
            onChange={(e) => navigate(buildPath.complaintsList(e.target.value))}
          >
            {filters.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        )}

        <select
          className="ty-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {COMPLAINT_STATUS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <input
          className="ty-input"
          placeholder="Search ticket / title / description"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <ErrorBox error={error} />

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <div className="ty-empty">No complaints match your filters.</div>
      ) : (
        <div className="ty-table-wrap">
          <table className="ty-table">
            <thead>
              <tr>
                <SortableTh label="Ticket" sortKey="ticket" activeKey={sort.key} direction={sort.dir} onSort={toggleSort} />
                <SortableTh label="Title" sortKey="title" activeKey={sort.key} direction={sort.dir} onSort={toggleSort} />
                <SortableTh label="Division" sortKey="division" activeKey={sort.key} direction={sort.dir} onSort={toggleSort} />
                <SortableTh label="Assigned To" sortKey="assigned" activeKey={sort.key} direction={sort.dir} onSort={toggleSort} />
                <SortableTh label="Status" sortKey="status" activeKey={sort.key} direction={sort.dir} onSort={toggleSort} />
                {can.canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((c) => (
                <tr key={c.id}>
                  <td data-label="Ticket">#{complaintSerial(c)}</td>
                  <td data-label="Title">
                    <Link to={buildPath.complaintDetails(c.id)}>
                      {c.complaint_title || '—'}
                    </Link>
                  </td>
                  <td data-label="Division">{c.division_name || '—'}</td>
                  <td data-label="Assigned">{c.assign_to_email || '—'}</td>
                  <td data-label="Status"><StatusBadge domain="complaint" value={c.status} /></td>
                  {/* External users can't edit a filed ticket — the row
                      title still links to the read-only detail view. */}
                  {can.canEdit && (
                    <td data-label="">
                      <Link className="ty-btn ty-btn--ghost ty-btn--sm" to={buildPath.complaintEdit(c.id)}>
                        Edit
                      </Link>
                    </td>
                  )}

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
